import Phaser from "phaser";

/**
 * Equipment overlay — draws hat / drink / carry-item attached to a character
 * with direction-aware anchor offsets.
 *
 *  Character sprite: 32×64, origin (0.5, 0.75) — the *feet* are the visible
 *  anchor for the player's body position. Head top is ~48px above feet.
 *
 *  Anchor offsets per direction (relative to character sprite root):
 *    HEAD_TOP[direction] — where the hat sits
 *    HAND[direction]     — where held items render
 *
 *  The character we use is the LimeZu char-N at 32×64 with feet at sprite y.
 *  Head is at y - 48; hand pivots based on which way you're facing.
 */

type Dir = "down" | "up" | "left" | "right";

// Anchor offsets calibrated for the procedural CharacterAvatar (Phase 10):
//   Feet at body.y, head occupies y range -36..-52 (rounded rect),
//   arms drawn at y range -28..-16, hands extend to x = ±10.
//
// Old LimeZu offsets (head=-36, hand=-2) are wrong for the procedural body —
// hat would float at chest level + drinks would sit at the floor.
//
// Hat sits at body.y - 50 (just below head top y=-52, on the crown).
// Hand items at body.y - 22 (mid-arm height, where a real hand would hold).
const HEAD: Record<Dir, { x: number; y: number }> = {
  down:  { x: 0,  y: -50 },
  up:    { x: 0,  y: -50 },
  left:  { x: -1, y: -50 },
  right: { x: 1,  y: -50 },
};

const HAND: Record<Dir, { x: number; y: number }> = {
  down:  { x: 8,   y: -22 },     // right hand visible (mid-torso/arm height)
  up:    { x: -8,  y: -22 },     // mirrored when facing away
  left:  { x: -9,  y: -23 },     // slightly forward of body
  right: { x: 9,   y: -23 },
};

/** Drinks have real pixel-art sprites (Kenney CC0 food pack). Map item id → image key. */
const DRINK_SPRITE: Record<string, string> = {
  drink_beer: "item-drink-beer",
  drink_champagne: "item-drink-champagne",
  drink_coffee: "item-drink-coffee",
  drink_boba: "item-drink-boba",
};

/** Preload all real-sprite images. Call once at scene preload(). */
export function preloadItemSprites(scene: Phaser.Scene) {
  scene.load.image("item-drink-beer",      "/art/items/drink-beer.png");
  scene.load.image("item-drink-champagne", "/art/items/drink-champagne.png");
  scene.load.image("item-drink-coffee",    "/art/items/drink-coffee.png");
  scene.load.image("item-drink-boba",      "/art/items/drink-boba.png");
}

/** A single character's equipment layer. */
export class ItemRenderer {
  private scene: Phaser.Scene;
  private hat: Phaser.GameObjects.Graphics;
  private hand: Phaser.GameObjects.Graphics;   // drink OR carry — drink takes precedence
  private handSprite: Phaser.GameObjects.Image | null = null;  // when drink uses real pixel art
  private currentHatId = "";
  private currentHandId = "";

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.hat = scene.add.graphics();
    this.hand = scene.add.graphics();
    this.hat.setDepth(55);
    this.hand.setDepth(56);
  }

  destroy() {
    this.hat.destroy();
    this.hand.destroy();
    this.handSprite?.destroy();
  }

  /** Reposition every frame. Call from scene.update() with the latest body pos + direction. */
  syncTo(bodyX: number, bodyY: number, dir: string) {
    const d = (dir as Dir) ?? "down";
    if (this.currentHatId) {
      const off = HEAD[d];
      this.hat.setPosition(bodyX + off.x, bodyY + off.y);
      // Mirror hat if facing left
      this.hat.setScale(d === "left" ? -1 : 1, 1);
    }
    if (this.currentHandId) {
      const off = HAND[d];
      this.hand.setPosition(bodyX + off.x, bodyY + off.y);
      this.hand.setScale(d === "left" ? -1 : 1, 1);
      if (this.handSprite) {
        this.handSprite.setPosition(bodyX + off.x, bodyY + off.y);
        this.handSprite.setFlipX(d === "left");
      }
    }
  }

  setHat(itemId: string) {
    if (this.currentHatId === itemId) return;
    this.currentHatId = itemId;
    this.hat.clear();
    if (!itemId) return;
    this.drawHat(itemId);
  }

  setHand(drinkId: string, carryId: string) {
    // Drink takes precedence over carry (you can hold one thing at a time)
    const id = drinkId || carryId;
    if (this.currentHandId === id) return;
    this.currentHandId = id;
    this.hand.clear();
    // Tear down any previous sprite layer
    if (this.handSprite) {
      this.handSprite.destroy();
      this.handSprite = null;
    }
    if (!id) return;
    // For drinks with real pixel-art sprites, render an Image instead of Graphics
    if (drinkId && DRINK_SPRITE[drinkId]) {
      this.handSprite = this.scene.add.image(0, 0, DRINK_SPRITE[drinkId]);
      this.handSprite.setOrigin(0.5, 0.5);
      this.handSprite.setDepth(56);
      // The drink sprites are 32×32 (16×16 source × 2 scale), but the LimeZu
      // chars are 32×64 — so 32×32 hand items would dominate. Scale down.
      this.handSprite.setScale(0.55);
      return;
    }
    if (drinkId) this.drawDrink(drinkId);
    else this.drawCarry(carryId);
  }

  // ────────────────── Hat shapes (drawn at 0,0 — anchor at brim centre) ──────────────────

  private drawHat(itemId: string) {
    const g = this.hat;
    switch (itemId) {
      case "hat_cap": {
        // Baseball cap — blue, with brim
        g.fillStyle(0x2563eb, 1);
        g.fillRoundedRect(-7, -3, 14, 6, 2);   // crown
        g.fillRect(-9, 1, 12, 2);              // brim sticks out one side
        g.lineStyle(1, 0x1e40af, 1);
        g.strokeRoundedRect(-7, -3, 14, 6, 2);
        break;
      }
      case "hat_chef": {
        // Chef hat — white puffy top
        g.fillStyle(0xffffff, 1);
        g.fillEllipse(0, -4, 14, 8);           // puff
        g.fillRect(-5, -1, 10, 4);             // band
        g.lineStyle(1, 0xd4d4d4, 1);
        g.strokeEllipse(0, -4, 14, 8);
        break;
      }
      case "hat_crown": {
        // Gold crown — 5 points + jewels
        g.fillStyle(0xffd700, 1);
        g.fillTriangle(-7, 2, -4, -4, -2, 2);
        g.fillTriangle(-2, 2, 0, -6, 2, 2);
        g.fillTriangle(2, 2, 4, -4, 7, 2);
        g.fillRect(-7, 2, 14, 3);              // band
        g.fillStyle(0xef4444, 1);
        g.fillCircle(0, -2, 1.4);              // ruby
        g.lineStyle(1, 0xa16207, 1);
        g.strokeRect(-7, 2, 14, 3);
        break;
      }
      case "hat_party": {
        // Party hat — pink cone with stripes
        g.fillStyle(0xff2e88, 1);
        g.fillTriangle(-5, 3, 5, 3, 0, -8);
        g.fillStyle(0xfde047, 1);
        g.fillCircle(0, -8, 1.5);              // pom-pom
        g.lineStyle(1, 0xa21caf, 1);
        g.strokeTriangle(-5, 3, 5, 3, 0, -8);
        break;
      }
      default: {
        // Unknown hat — neutral grey beanie
        g.fillStyle(0x6b7280, 1);
        g.fillRoundedRect(-6, -3, 12, 6, 2);
      }
    }
  }

  // ────────────────── Drink shapes (anchor: hand point) ──────────────────

  private drawDrink(itemId: string) {
    const g = this.hand;
    switch (itemId) {
      case "drink_beer": {
        // Frosty beer mug — amber with foam
        g.fillStyle(0xf59e0b, 1);
        g.fillRect(-3, -4, 6, 8);              // body
        g.fillStyle(0xffffff, 1);
        g.fillRect(-3, -5, 6, 2);              // foam
        g.lineStyle(1, 0x000000, 1);
        g.strokeRect(-3, -4, 6, 8);
        g.strokeRect(3, -2, 2, 4);             // handle
        break;
      }
      case "drink_champagne": {
        // Champagne flute — narrow stem
        g.fillStyle(0xfde68a, 0.9);
        g.fillTriangle(-2, -5, 2, -5, 0, 0);   // bowl
        g.lineStyle(1, 0x6b7280, 1);
        g.lineBetween(0, 0, 0, 4);             // stem
        g.strokeTriangle(-2, -5, 2, -5, 0, 0);
        break;
      }
      case "drink_coffee": {
        // Coffee cup — brown with handle + steam
        g.fillStyle(0x422006, 1);
        g.fillRoundedRect(-3, -4, 6, 7, 1);    // cup
        g.fillStyle(0x6b21a8, 1);              // (purple lid? we'll do white rim)
        g.fillRect(-3, -4, 6, 1);              // rim
        g.lineStyle(1, 0x111827, 1);
        g.strokeRoundedRect(-3, -4, 6, 7, 1);
        g.strokeRect(3, -2, 2, 3);             // handle
        // Steam
        g.lineStyle(1, 0xc4b5fd, 0.8);
        g.lineBetween(-1, -6, -1, -8);
        g.lineBetween(1, -6, 1, -8);
        break;
      }
      case "drink_boba": {
        // Bubble tea — tall cup with straw + dots
        g.fillStyle(0xfde68a, 1);              // tan boba liquid
        g.fillRoundedRect(-3, -5, 6, 8, 1);
        g.fillStyle(0x422006, 1);              // boba pearls
        g.fillCircle(-1, 1, 0.8);
        g.fillCircle(1, 2, 0.8);
        g.fillStyle(0xff2e88, 1);              // pink straw
        g.fillRect(0, -8, 1, 4);
        g.lineStyle(1, 0x6b7280, 1);
        g.strokeRoundedRect(-3, -5, 6, 8, 1);
        break;
      }
      default: {
        g.fillStyle(0xa78bfa, 1);
        g.fillCircle(0, 0, 3);
      }
    }
  }

  // ────────────────── Carry items ──────────────────

  private drawCarry(itemId: string) {
    const g = this.hand;
    switch (itemId) {
      case "carry_mic": {
        // Wireless mic — black handle + silver head
        g.fillStyle(0xe5e7eb, 1);              // silver mesh
        g.fillCircle(0, -4, 2.5);
        g.fillStyle(0x111827, 1);              // black grip
        g.fillRect(-1, -2, 2, 6);
        g.lineStyle(1, 0xa16207, 1);
        g.strokeCircle(0, -4, 2.5);
        // LED
        g.fillStyle(0xef4444, 1);
        g.fillCircle(0, -1.5, 0.5);
        break;
      }
      case "carry_cane": {
        // Walking cane — brown wood with hook handle
        g.fillStyle(0x78350f, 1);
        g.fillRect(-0.5, -8, 1.5, 14);          // shaft
        g.lineStyle(2, 0x78350f, 1);
        g.strokeCircle(2, -8, 2);               // hook handle
        g.fillStyle(0xfde047, 1);              // gold tip
        g.fillRect(-0.5, 5, 1.5, 1.5);
        break;
      }
      case "carry_phone": {
        // Smartphone — black with cyan screen
        g.fillStyle(0x111827, 1);
        g.fillRoundedRect(-2, -4, 4, 7, 0.5);
        g.fillStyle(0x06b6d4, 1);
        g.fillRect(-1.5, -3.5, 3, 5.5);
        g.lineStyle(1, 0x0e7490, 1);
        g.strokeRoundedRect(-2, -4, 4, 7, 0.5);
        break;
      }
      case "carry_book": {
        // Book — red cover with pages
        g.fillStyle(0xdc2626, 1);
        g.fillRect(-3, -3, 6, 5);
        g.fillStyle(0xffffff, 1);
        g.fillRect(-2.5, -2.5, 5, 4);
        g.lineStyle(1, 0x991b1b, 1);
        g.strokeRect(-3, -3, 6, 5);
        // gold band
        g.lineStyle(0.5, 0xfde047, 1);
        g.lineBetween(-2.5, 0, 2.5, 0);
        break;
      }
      default: {
        g.fillStyle(0x9ca3af, 1);
        g.fillRect(-2, -3, 4, 6);
      }
    }
  }
}
